import { useEffect, useRef } from "react";
import { getAccessTokenMsRemaining, silentRefresh } from "@/utils/auth";
import { useAuthLogout } from "@/hooks/useAuthLogout";

// Refresh this long before expiry.
const REFRESH_LEAD_MS = 60_000;
// Random spread added to the lead so multiple tabs DON'T fire at the same instant.
const JITTER_MS = 20_000;
// If the token still has more than this left when our timer fires, another tab
// already refreshed it (shared cookie) — skip our refresh and just reschedule.
const FRESH_THRESHOLD_MS = 90_000;
// Floor on the next-timer delay — stops a tight loop if a token ever comes back
// already near-expiry. Never delays a real refresh (healthy 15m token reschedules
// ~14m out, far above this).
const MIN_RESCHEDULE_MS = 10_000;
// Server unreachable → don't kill the session, just try again shortly. A dev
// restart or a blip is over long before the token actually expires.
const NETWORK_RETRY_MS = 15_000;

export const useProactiveTokenRefresh = () => {
  const logout = useAuthLogout();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let stopped = false;

    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      const msRemaining = getAccessTokenMsRemaining();
      if (msRemaining === null) return; // no token — nothing to schedule

      const lead = REFRESH_LEAD_MS + Math.random() * JITTER_MS;
      const delay = Math.max(MIN_RESCHEDULE_MS, msRemaining - lead);

      timerRef.current = setTimeout(tick, delay);
    };

    const tick = async () => {
      if (stopped) return;

      // Re-read cookie — another tab may have refreshed while our timer waited.
      const msRemaining = getAccessTokenMsRemaining();
      if (msRemaining === null) {
        await logout();
        return;
      }
      if (msRemaining > FRESH_THRESHOLD_MS) {
        // Already fresh (another tab did it) — reschedule, no network call.
        schedule();
        return;
      }

      const result = await silentRefresh();
      if (stopped) return;

      if (result.ok) {
        schedule();
        return;
      }

      // Refresh failed — but did another tab refresh meanwhile?
      const afterMs = getAccessTokenMsRemaining();
      if (afterMs !== null && afterMs > FRESH_THRESHOLD_MS) {
        schedule();
        return;
      }

      // Never got a verdict from the BE — retry soon rather than signing the
      // user out mid-work. If the API stays down, the next request they make
      // routes them to the connection-lost screen.
      if (result.reason === "network") {
        timerRef.current = setTimeout(tick, NETWORK_RETRY_MS);
        return;
      }

      await logout();
    };

    schedule();

    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [logout]);
};
